import { inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@core/auth/auth.service';
import { UserProfile } from '@core/user/user.models';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concat, EMPTY, from, Observable, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserStore {
  private readonly auth: AuthService = inject(AuthService);
  private translateService = inject(TranslateService);

  public readonly isInitialized = signal(false);
  public readonly userProfile = signal<UserProfile | null>(null);

  public initialize() {
    if (this.isInitialized()) {
      return EMPTY as Observable<void>;
    }

    return concat(this.createUserProfile()).pipe(
      tap({ complete: () => this.isInitialized.set(true) }),
      catchError((error) => {
        console.error('Error initializing user store:', error);
        return throwError(() => error);
      }),
    );
  }

  // Reloads from the API, for example after an organization switch.
  public reinitialize() {
    return this.createUserProfile().pipe(
      catchError((error) => {
        console.error('Error reinitializing user store:', error);
        return throwError(() => error);
      }),
    );
  }

  private createUserProfile() {
    return from(this.auth.getUserAttributes()).pipe(
      tap((user) => {
        const profile = user as unknown as UserProfile;
        this.userProfile.set(profile);
        // Set the language based on the user profile, fallback to browser language or default to German
        const userLang = profile.attributes.locale[0] || 'de-DE';
        this.translateService.use(userLang);
      }),
      catchError((error) => {
        console.error('Error creating user profile:', error);
        return throwError(() => error);
      }),
    );
  }
}
